<h2 mat-dialog-title>
  <mat-icon>person_add</mat-icon>
  Add New Person
</h2>

<mat-dialog-content class="dialog-content">
  <!-- Loading Spinner -->
  @if (loading || saving) {
    <div class="loading-overlay">
      <mat-spinner diameter="40"></mat-spinner>
      <p>{{ saving ? 'Saving person...' : 'Loading...' }}</p>
    </div>
  }

  <!-- Top Action Buttons -->
  <div class="top-actions">
    <button mat-stroked-button color="accent" (click)="cancel()" type="button">
      <mat-icon>close</mat-icon>
      Cancel
    </button>
    <button mat-raised-button color="primary"
            [disabled]="!isFormValid() || saving"
            (click)="onSubmit()" type="button">
      <mat-icon>save</mat-icon>
      Save Person
    </button>
  </div>

  <form [formGroup]="form">

    <!-- Personal Information Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>person</mat-icon>
          Personal Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid three-column">
          <mat-form-field appearance="outline">
            <mat-label>Prefix</mat-label>
            <input matInput
                   type="text"
                   formControlName="prefix"
                   [matAutocomplete]="prefixAuto"
                   placeholder="Mr., Ms., Dr., etc.">
            <mat-autocomplete #prefixAuto="matAutocomplete">
              @for (prefix of filteredPrefixes; track prefix) {
                <mat-option [value]="prefix">{{ prefix }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>First Name <span class="required-indicator">*</span></mat-label>
            <input matInput
                   formControlName="firstName"
                   placeholder="Enter first name"
                   required>
            <mat-icon matSuffix>person</mat-icon>
            @if (form.get('firstName')?.hasError('required')) {
              <mat-error>First name is required</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Middle Name</mat-label>
            <input matInput
                   formControlName="middleName"
                   placeholder="Enter middle name">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Last Name <span class="required-indicator">*</span></mat-label>
            <input matInput
                   formControlName="lastName"
                   placeholder="Enter last name"
                   required>
            <mat-icon matSuffix>person</mat-icon>
            @if (form.get('lastName')?.hasError('required')) {
              <mat-error>Last name is required</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Suffix</mat-label>
            <input matInput
                   type="text"
                   formControlName="suffix"
                   [matAutocomplete]="suffixAuto"
                   placeholder="Jr., Sr., III, etc.">
            <mat-autocomplete #suffixAuto="matAutocomplete">
              @for (suffix of filteredSuffixes; track suffix) {
                <mat-option [value]="suffix">{{ suffix }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Salutation</mat-label>
            <input matInput
                   formControlName="salutation"
                   placeholder="Dear, Hi, etc.">
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Full Name (Auto-generated)</mat-label>
            <input matInput
                   formControlName="fullName"
                   placeholder="Auto-generated from first/middle/last"
                   readonly>
            <mat-icon matSuffix>auto_fix_high</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Friendly Name</mat-label>
            <input matInput
                   formControlName="friendlyName"
                   placeholder="Preferred display name">
          </mat-form-field>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Professional Information Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>work</mat-icon>
          Professional Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid three-column">
          <mat-form-field appearance="outline">
            <mat-label>Title</mat-label>
            <input matInput
                   type="text"
                   formControlName="title"
                   [matAutocomplete]="titleAuto"
                   placeholder="CEO, Manager, Director, etc.">
            <mat-autocomplete #titleAuto="matAutocomplete">
              @for (title of filteredTitles; track title) {
                <mat-option [value]="title">{{ title }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Function</mat-label>
            <input matInput
                   type="text"
                   formControlName="function"
                   [matAutocomplete]="functionAuto"
                   placeholder="Signatory, Coordinator, etc.">
            <mat-autocomplete #functionAuto="matAutocomplete">
              @for (func of filteredFunctions; track func) {
                <mat-option [value]="func">{{ func }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Department</mat-label>
            <input matInput
                   type="text"
                   formControlName="department"
                   [matAutocomplete]="deptAuto"
                   placeholder="Select department">
            <mat-autocomplete #deptAuto="matAutocomplete">
              @for (dept of filteredDepartments; track dept) {
                <mat-option [value]="dept">{{ dept }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Contact Information Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>contact_phone</mat-icon>
          Contact Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid two-column">
          <mat-form-field appearance="outline">
            <mat-label>Email</mat-label>
            <input matInput
                   type="email"
                   formControlName="email"
                   placeholder="email@example.com">
            <mat-icon matSuffix>email</mat-icon>
            @if (form.get('email')?.hasError('email')) {
              <mat-error>Please enter a valid email</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Secondary Email</mat-label>
            <input matInput
                   type="email"
                   formControlName="email2"
                   placeholder="alternate@example.com">
            <mat-icon matSuffix>email</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Primary Phone</mat-label>
            <input matInput
                   formControlName="primaryPhone"
                   placeholder="(555) 123-4567">
            <mat-icon matSuffix>phone</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Office Phone</mat-label>
            <input matInput
                   formControlName="officePhone"
                   placeholder="(555) 123-4567">
            <mat-icon matSuffix>business</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Mobile Phone</mat-label>
            <input matInput
                   formControlName="mobilePhone"
                   placeholder="(555) 123-4567">
            <mat-icon matSuffix>smartphone</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Fax Phone</mat-label>
            <input matInput
                   formControlName="faxPhone"
                   placeholder="(555) 123-4567">
            <mat-icon matSuffix>print</mat-icon>
          </mat-form-field>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Primary Address Section -->
    <mat-card class="form-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>location_on</mat-icon>
          Primary Address
        </mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="form-grid">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Street Address</mat-label>
            <input matInput
                   formControlName="address"
                   placeholder="123 Main Street">
            <mat-icon matSuffix>home</mat-icon>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>City</mat-label>
            <input matInput
                   formControlName="city"
                   placeholder="City name">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>State</mat-label>
            <input matInput
                   type="text"
                   formControlName="state"
                   [matAutocomplete]="stateAuto"
                   placeholder="Select state">
            <mat-autocomplete #stateAuto="matAutocomplete">
              @for (state of filteredStates; track state) {
                <mat-option [value]="state">{{ state }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>ZIP Code</mat-label>
            <input matInput
                   formControlName="zip"
                   placeholder="12345">
          </mat-form-field>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Secondary Address Section (Expansion Panel) -->
    <mat-expansion-panel class="expansion-card">
      <mat-expansion-panel-header>
        <mat-panel-title>
          <mat-icon>add_location</mat-icon>
          Secondary Address (Optional)
        </mat-panel-title>
      </mat-expansion-panel-header>
      <div class="expansion-content">
        <div class="form-grid">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Secondary Street Address</mat-label>
            <input matInput
                   formControlName="secondaryAddress"
                   placeholder="456 Oak Avenue">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>City</mat-label>
            <input matInput
                   formControlName="secondaryCity"
                   placeholder="City name">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>State</mat-label>
            <input matInput
                   type="text"
                   formControlName="secondaryState"
                   [matAutocomplete]="stateAuto2"
                   placeholder="Select state">
            <mat-autocomplete #stateAuto2="matAutocomplete">
              @for (state of filteredStates; track state) {
                <mat-option [value]="state">{{ state }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>ZIP Code</mat-label>
            <input matInput
                   formControlName="secondaryZip"
                   placeholder="12345">
          </mat-form-field>
        </div>
      </div>
    </mat-expansion-panel>

    <!-- Relationships Section (Expansion Panel) -->
    <!-- Opened by default, matching new-person-full-page: Primary Vendor is a
         routinely-used field and reads as missing when collapsed out of sight. -->
    <mat-expansion-panel class="expansion-card" expanded>
      <mat-expansion-panel-header>
        <mat-panel-title>
          <mat-icon>link</mat-icon>
          Relationships & Assignments
        </mat-panel-title>
      </mat-expansion-panel-header>
      <div class="expansion-content">
        <div class="form-grid two-column">
          <mat-form-field appearance="outline">
            <mat-label>Primary Vendor</mat-label>
            <input matInput
                   type="text"
                   formControlName="primaryVendor"
                   [matAutocomplete]="vendorAuto"
                   (input)="filterParties($event)"
                   placeholder="Search for vendor">
            <mat-icon matSuffix>business</mat-icon>
            <mat-autocomplete #vendorAuto="matAutocomplete" [displayWith]="displayPartyFn">
              @for (vendor of filteredParties; track vendor.vendorNum) {
                <mat-option [value]="vendor">{{ vendor | partyLabel }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Primary Responsible Party</mat-label>
            <input matInput
                   type="text"
                   formControlName="primaryResponsibleParty"
                   [matAutocomplete]="userAuto"
                   (input)="filterUsers($event)"
                   placeholder="Search for user">
            <mat-icon matSuffix>person</mat-icon>
            <mat-autocomplete #userAuto="matAutocomplete" [displayWith]="displayUserFn">
              @for (user of filteredUsers; track user.email) {
                <mat-option [value]="user">{{ user.shortFriendlyName }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        </div>
      </div>
    </mat-expansion-panel>

    <!-- Additional Details Section (Expansion Panel) -->
    <mat-expansion-panel class="expansion-card">
      <mat-expansion-panel-header>
        <mat-panel-title>
          <mat-icon>description</mat-icon>
          Additional Details
        </mat-panel-title>
      </mat-expansion-panel-header>
      <div class="expansion-content">
        <div class="form-grid">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Description</mat-label>
            <textarea matInput
                      formControlName="description"
                      rows="3"
                      placeholder="Enter description or notes"></textarea>
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Other Information</mat-label>
            <textarea matInput
                      formControlName="other"
                      rows="3"
                      placeholder="Additional information"></textarea>
          </mat-form-field>
        </div>
      </div>
    </mat-expansion-panel>

  </form>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-stroked-button color="accent" (click)="cancel()">
    <mat-icon>close</mat-icon>
    Cancel
  </button>
  <button mat-raised-button color="primary"
          [disabled]="!isFormValid() || saving"
          (click)="onSubmit()">
    <mat-icon>save</mat-icon>
    Save Person
  </button>
</mat-dialog-actions>
